"use client";

import { FC, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Logo from "shared/Logo/Logo";
import { useData } from "data/data-provider";
import LocationInput from "components/HeroSearchForm/LocationInput";
import RentalCarDatesRangeInput from "components/HeroSearchForm/(car-search-form)/RentalCarDatesRangeInput";
import RentalCarTimesRangeInput from "components/HeroSearchForm/(car-search-form)/RentalCarTimesRangeInput";
import DropOffLocationInput from "components/HeroSearchForm/DropOffLocationInput";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

export interface MobileSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileSearchModal: FC<MobileSearchModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const {
    dropOffLocation,
    setPickupLocation,
    setDropoffLocation,
    setSearchedPickupLocation,
    setSearchedDropoffLocation,
    pickupLocation,
    setDropoffLocationType,
    setSameDropoffLocation,
    dropoffLocationType,
    startDate,
    endDate,
    callAvailableFleetAPI,
    loadingAvailableFleet,
    hasSelectedStartTime,
    hasSelectedEndTime,
  } = useData();

  const isDdropOffdifferent = dropoffLocationType === false;

  const handleSearch = () => {
    if (pickupLocation === null) {
      toast.error("Please enter Pick up location");
    } else if (dropOffLocation === null && dropoffLocationType === false) {
      toast.error("Please enter Return location");
    } else if (startDate === null && endDate === null) {
      toast.error("Please enter start and end date");
    } else if (!hasSelectedStartTime || !hasSelectedEndTime) {
      toast.error("Please select pickup and return times");
    } else if (pickupLocation !== null) {
      onClose();
      callAvailableFleetAPI(navigate);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/80" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full h-screen transform overflow-y-auto bg-[#121212] text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between p-4">
                  <Logo />
                  <button
                    onClick={onClose}
                    className="p-2 rounded-full border border-neutral-600 hover:bg-neutral-800 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-white" />
                  </button>
                </div>

                {/* Search Form Content */}
                <div className="px-6 py-4">
                  {/* Pickup Location */}
                  <div className="mb-5 mob-mb-10">
                    <label className="block text-white text-base font-semibold mb-2">
                      Pickup Location
                    </label>
                    <LocationInput
                      placeHolder="Choose Location"
                      className="w-full"
                      sameDropOffLocation={dropoffLocationType === false}
                      variant="box"
                    />
                  </div>

                  {/* Different Return Location Link */}
                  {!isDdropOffdifferent && (
                    <button
                      type="button"
                      onClick={() => {
                        setDropoffLocationType(false);
                        setSameDropoffLocation(false);
                        setPickupLocation(null);
                        setDropoffLocation(null);
                        setSearchedDropoffLocation("");
                        setSearchedPickupLocation("");
                      }}
                      className="text-[#B91C1C] text-sm font-medium mb-5 hover:underline block"
                    >
                      + Different Return Location
                    </button>
                  )}

                  {/* Return Location (if different) */}
                  {isDdropOffdifferent && (
                    <div className="mb-5">
                      <label className="block text-white text-base font-semibold mb-2">
                        Return Location
                      </label>
                      <DropOffLocationInput
                        placeHolder="Choose Location"
                        className="w-full"
                        variant="box"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setDropoffLocationType(true);
                          setSameDropoffLocation(true);
                          setDropoffLocation(pickupLocation);
                          setSearchedDropoffLocation(
                            pickupLocation?.name ?? "",
                          );
                        }}
                        className="text-neutral-400 text-sm mt-2 hover:underline"
                      >
                        - Same Return Location
                      </button>
                    </div>
                  )}

                  {/* Pickup Date & Time */}
                  <div className="mb-5">
                    <label className="block text-white text-base font-semibold mb-2">
                      Pickup Date & Time
                    </label>
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <RentalCarDatesRangeInput
                          variant="pickup"
                          hideLabel
                          className="w-full"
                          pickupPlaceholder={"Pickup Date"}
                          dropoffPlaceholder={"Return Date"}
                          searchBar={true}
                          home={true}
                        />
                      </div>
                      <div className="flex-1">
                        <RentalCarTimesRangeInput
                          dropOffLocationType={dropoffLocationType}
                          variant="pickup"
                          hideLabel
                          className="w-full"
                          searchBar={true}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Return Date & Time */}
                  <div className="mb-8">
                    <label className="block text-white text-base font-semibold mb-2">
                      Return Date & Time
                    </label>
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <RentalCarDatesRangeInput
                          variant="dropoff"
                          hideLabel
                          className="w-full"
                          pickupPlaceholder={"Pickup Date"}
                          dropoffPlaceholder={"Return Date"}
                          searchBar={true}
                          home={true}
                        />
                      </div>
                      <div className="flex-1">
                        <RentalCarTimesRangeInput
                          dropOffLocationType={dropoffLocationType}
                          variant="dropoff"
                          hideLabel
                          className="w-full"
                          searchBar={true}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Search Button */}
                  <button
                    onClick={handleSearch}
                    disabled={loadingAvailableFleet}
                    className="w-full bg-[#B22222] hover:bg-[#9a1d1d] text-white font-semibold py-4 rounded-lg transition-colors flex items-center justify-center"
                  >
                    {loadingAvailableFleet ? (
                      <svg
                        aria-hidden="true"
                        className="w-6 h-6 text-gray-200 animate-spin fill-white"
                        viewBox="0 0 100 101"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                          fill="currentColor"
                        />
                        <path
                          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                          fill="currentFill"
                        />
                      </svg>
                    ) : (
                      <span className="flex space-x-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-6 h-6 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
                          />
                        </svg>
                        <span>View Cars</span>
                      </span>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default MobileSearchModal;
